import React, { useState, useEffect } from 'react';
import { useDataProvider, useNotify, Loading } from 'react-admin';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Alert } from '@mui/material';
import { apiClient } from '../../framework/api/api.client';

/**
 * My Location - Single location view for location-level environments
 * 
 * Allows location instances to manage their own location record
 * without seeing a list of all locations (security isolation)
 */
export const MyLocation = () => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const navigate = useNavigate();
  const [locationId, setLocationId] = useState<string | null>(null);
  const [location, setLocation] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadCurrentLocation();
  }, []);

  useEffect(() => {
    if (locationId) {
      loadLocation();
    }
  }, [locationId]);

  const loadCurrentLocation = async () => {
    try {
      // Get location ID from backend authenticated context
      const data = await apiClient.call('/api/location-info/current');
      
      if (data && data.locationId) {
        console.log('✅ Loaded location from authenticated context:', data);
        setLocationId(data.locationId);
      } else {
        setError('No location ID found in authenticated context. Check LOCATION env variable.');
        setLoading(false);
      }
    } catch (err: any) {
      console.error('Failed to load location:', err);
      setError(err.message || 'Failed to load location information');
      setLoading(false);
    }
  };

  const loadLocation = async () => {
    try {
      // Use getList with filter by id (UUID) instead of getOne which expects ObjectId
      const { data } = await dataProvider.getList('location', {
        filter: { id: locationId },
        pagination: { page: 1, perPage: 1 },
        sort: { field: 'name', order: 'ASC' }
      });
      
      if (data && data.length > 0) {
        setLocation(data[0]);
        // Redirect to the actual location edit page
        navigate(`/location/${data[0].id}`);
      } else {
        console.error('Location not found with id:', locationId);
        setError('Location not found');
      }
    } catch (error: any) {
      console.error('Failed to load location:', error);
      setError(error.message || 'Failed to load location');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <Loading />;
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, textAlign: 'center' }}>
      <Typography>Redirecting to location settings...</Typography>
    </Box>
  );
};

